/**
 * **Firestore를 다루는 법 — 컬렉션이 무엇이든 같은 것들.**
 * `sections/**`(무엇을 하고 싶다) → `actions/firestore/sdk/**`(도메인 — 어느 컬렉션 · 문서 id
 * 규칙 · 필드 드리프트) → 이 파일(플랫폼 — in은 30개씩 · 문서 id는 뒤에) → `firebase/firestore`.
 *
 * ```
 *   여기(묶는다)     어떻게 읽는가 — 청크 · 병렬 · 합치기 · 상한
 *   창구(나눈다)     무엇을 읽는가 — 컬렉션 · 필드 · 행 빚기
 * ```
 *
 * **행 빚기는 반드시 인자로 받는다.** `readUserRow`처럼 필드 드리프트를 흡수하는
 * 리더는 도메인 지식이라 이 아래로 내려오면 안 된다.
 *
 * ⚠️ **창구의 공통 부모가 아니다** — 이 파일은 "Firestore 다루는 법"이지 "우리 데이터가
 * 무엇인가"가 아니다. 문서 id 규칙만 해도 창구마다 다르다: `{플랫폼}_{계정}`(hub-profile) ·
 * `.`과 `/`를 `_`로 바꾸는 것(fullList) · 밑줄 둘(`슬러그__지역`, keyword-trend).
 * 그건 각 창구가 알아야 할 값어치라 여기로 끌어오면 안 된다.
 */

import type {
  Query,
  DocumentData,
  QuerySnapshot,
  DocumentSnapshot,
  CollectionReference,
} from 'firebase/firestore';

import { doc, query, where, getDoc, getDocs } from 'firebase/firestore';

// ---- Firestore 제약 ----------------------------------------------------

/**
 * `where(..., 'in', [...])` 한 번에 넣을 수 있는 값의 수.
 *
 * ⚠️ **Firestore가 정한 한도지 우리 정책이 아니다.** 실제 한도는 30이다.
 */
/**
 * `in` 한 번에 넣을 수 있는 값의 수 — `array-contains-any` 와 같은 30이다.
 * ⚠️ 31부터 거절된다.
 */
export const IN_QUERY_LIMIT = 30;

/**
 * `array-contains-any` 한 번에 넣을 수 있는 값의 수.
 *
 * ⚠️ **10이 아니라 30이다.** 31부터 `'ARRAY_CONTAINS_ANY' supports up to 30 comparison
 * values`로 거절된다. 리포에 남아 있는 10을 보고 그 크기로 쪼개면 필요한 것의 3배를 던진다.
 */
export const ARRAY_CONTAINS_ANY_LIMIT = 30;

/** 배열을 크기별로 자른다 */
export function chunkArray<T>(items: T[], size: number): T[][] {
  const list = Array.isArray(items) ? items : [];
  const step = Math.max(1, Number(size) || 1);
  const out: T[][] = [];
  for (let i = 0; i < list.length; i += step) out.push(list.slice(i, i + step));
  return out;
}

/** 문자열 id 목록을 다듬고 중복을 없앤다 — 빈 값은 버린다 */
export function normalizeIds(ids: unknown[] = []): string[] {
  return [...new Set(ids.map((v) => String(v || '').trim()).filter(Boolean))];
}

// ---- 행 변환 -----------------------------------------------------------

/** 문서 id를 얹은 평범한 객체 */
export type Row = DocumentData & { id: string };

/**
 * 스냅샷 하나를 행으로. **문서 id는 항상 마지막에 얹는다.**
 *
 * ⚠️ 순서가 규칙이다. `{ id: snap.id, ...snap.data() }`로 쓰면 **데이터에 `id` 필드가
 * 있을 때 그것이 문서 id를 덮어쓴다.** 컨텐츠 플래너의 채널 문서가 그렇고(`id: 'reddit'`),
 * 화면이 'reddit'을 문서 id로 알면 없는 문서를 지우려 든다 — **Firestore는 없는 문서
 * 삭제도 성공으로 치므로 성공 토스트만 뜨고 아무것도 안 지워진다.**
 */
export function rowOf(snapshot: DocumentSnapshot): Row {
  return { ...snapshot.data(), id: snapshot.id };
}

/** 질의 결과를 행 배열로 — 문서 id는 항상 마지막에 */
export function rowsOf(snapshot: QuerySnapshot): Row[] {
  return snapshot.docs.map((d) => ({ ...d.data(), id: d.id }));
}

// ---- 여러 문서를 한 번에 ------------------------------------------------

/**
 * `in` 질의를 상한만큼 잘라 병렬로 던지고 합친다.
 *
 * `field`는 문서 id로 찾을 때 `'__name__'`, 필드로 찾을 때 그 필드 이름이다.
 * `toRow`를 주면 그것으로 행을 빚고, 없으면 `rowOf`를 쓴다 — **필드 드리프트를
 * 흡수하는 리더가 있는 컬렉션은 반드시 넘길 것.**
 *
 * 중복 id는 걸러지고, 빈 목록이면 질의하지 않는다.
 */
export async function queryInChunks<T = Row>(
  collectionRef: CollectionReference,
  field: string,
  ids: unknown[],
  toRow?: (snapshot: DocumentSnapshot) => T
): Promise<T[]> {
  const list = normalizeIds(ids);
  if (list.length === 0) return [];

  const build = toRow ?? ((snapshot: DocumentSnapshot) => rowOf(snapshot) as unknown as T);

  const batches = await Promise.all(
    chunkArray(list, IN_QUERY_LIMIT).map(async (batchIds) => {
      const snapshot = await getDocs(query(collectionRef as unknown as Query, where(field, 'in', batchIds)));
      return snapshot.docs.map(build);
    })
  );

  return batches.flat();
}

/**
 * 문서 id 여럿을 `chunk` 개씩 병렬로 집어 **있는 문서의 값만** 돌려준다(차례는 `ids` 를 따른다).
 * 한 문서의 읽기 실패는 전체를 깨지 않는다 — 그 문서만 빠지고, 실패는 **부름 한 번에 한 번만** `onFailed` 로 알린다
 * (규칙에 막히면 문서마다 실패한다 — 문서마다 알리면 로그가 쏟아진다).
 * @param chunk 한 번에 병렬로 던지는 수 — 묶음끼리는 차례로 돈다
 */
export async function getDocsByIds(
  collectionRef: CollectionReference,
  ids: readonly string[],
  { chunk, onFailed }: { chunk: number; onFailed?: (failedIds: string[], firstError: unknown) => void },
): Promise<DocumentData[]> {
  const out: DocumentData[] = [];
  const failed: string[] = [];
  let firstError: unknown = null;
  for (const batch of chunkArray([...ids], chunk)) {
    const snaps = await Promise.all(
      batch.map(async (id) => {
        try {
          return await getDoc(doc(collectionRef, id));
        } catch (error) {
          if (failed.length === 0) firstError = error;
          failed.push(id);
          return null;
        }
      }),
    );
    snaps.forEach((snap) => {
      if (snap?.exists()) out.push(snap.data());
    });
  }
  if (failed.length > 0) onFailed?.(failed, firstError);
  return out;
}

// 배열 이어붙이기(`appendToArrayField`)는 «쓰기»라 `firestore-write.ts`(④-e 게이트웨이)에 있다 — kit 에는 생짜 쓰기를 두지 않는다.
